import { useState } from "react";
import { CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { submitLead, formatApiErrorDetail } from "@/lib/api";

const TRACKS = [
  { id: "build", label: "Build / Renovate" },
  { id: "grow", label: "Grow (Digital & AI)" },
  { id: "both", label: "Both" },
];

export function LeadForm({ defaultTrack = "both", source = "contact_form", projectType = "", compact = false }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    track: defaultTrack,
    message: "",
    company_website: "", // honeypot — must stay empty
    fax: "",             // honeypot — must stay empty
  });
  const [status, setStatus] = useState("idle"); // idle | loading | done | error
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      await submitLead({ ...form, source, ...(projectType && { project_type: projectType }) });
      setStatus("done");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Try again.");
      setStatus("error");
    }
  };

  if (status === "done") {
    return (
      <div className="border border-primary/40 bg-primary/5 p-8 text-center" data-testid="lead-form-success">
        <CheckCircle size={40} className="text-primary mx-auto mb-4" weight="light" />
        <h3 className="font-display uppercase tracking-tight text-2xl">Request received</h3>
        <p className="mt-3 text-muted-foreground">
          Thanks {form.name.split(" ")[0] || "there"} — we'll reach out within one business day to
          book your free consultation. No surprises, no pressure.
        </p>
      </div>
    );
  }

  const inputCls =
    "w-full bg-secondary/60 border border-border focus:border-primary outline-none px-4 h-12 text-foreground placeholder:text-muted-foreground/70 transition-colors";

  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid="lead-form">
      {/* Honeypot fields — hidden from humans, tempting to bots. Leave empty. */}
      <div aria-hidden="true" className="hp-trap" tabIndex={-1}>
        <label>
          Company Website
          <input
            type="text"
            name="company_website"
            tabIndex={-1}
            autoComplete="off"
            value={form.company_website}
            onChange={set("company_website")}
            data-testid="lead-hp-company"
          />
        </label>
        <label>
          Fax
          <input
            type="text"
            name="fax"
            tabIndex={-1}
            autoComplete="off"
            value={form.fax}
            onChange={set("fax")}
            data-testid="lead-hp-fax"
          />
        </label>
      </div>
      <div className={`grid gap-4 ${compact ? "" : "sm:grid-cols-2"}`}>
        <div>
          <label className="font-mono-tech text-[11px] tracking-widest uppercase text-muted-foreground block mb-2">
            Name *
          </label>
          <input required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} data-testid="lead-name" />
        </div>
        <div>
          <label className="font-mono-tech text-[11px] tracking-widest uppercase text-muted-foreground block mb-2">
            Email *
          </label>
          <input required type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} data-testid="lead-email" />
        </div>
      </div>

      <div className={`grid gap-4 ${compact ? "" : "sm:grid-cols-2"}`}>
        <div>
          <label className="font-mono-tech text-[11px] tracking-widest uppercase text-muted-foreground block mb-2">
            Phone
          </label>
          <input value={form.phone} onChange={set("phone")} placeholder="(403) 000-0000" className={inputCls} data-testid="lead-phone" />
        </div>
        <div>
          <label className="font-mono-tech text-[11px] tracking-widest uppercase text-muted-foreground block mb-2">
            I need to…
          </label>
          <select value={form.track} onChange={set("track")} className={inputCls} data-testid="lead-track">
            {TRACKS.map((t) => (
              <option key={t.id} value={t.id} className="bg-background">
                {t.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="font-mono-tech text-[11px] tracking-widest uppercase text-muted-foreground block mb-2">
          Tell us about the project
        </label>
        <textarea
          value={form.message}
          onChange={set("message")}
          rows={compact ? 3 : 4}
          placeholder="Goals, scope, timeline, anything helpful…"
          className="w-full bg-secondary/60 border border-border focus:border-primary outline-none px-4 py-3 text-foreground placeholder:text-muted-foreground/70 transition-colors"
          data-testid="lead-message"
        />
      </div>

      {status === "error" && (
        <p className="text-sm text-destructive font-mono-tech" data-testid="lead-error">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "loading"}
        data-testid="lead-submit"
        className="group inline-flex items-center gap-3 bg-primary text-primary-foreground font-mono-tech text-xs tracking-widest uppercase px-7 h-12 hover:bg-[hsl(16,100%,55%)] transition-colors disabled:opacity-60"
      >
        {status === "loading" ? "Sending…" : "Get my free quote"}
        <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
      </button>
      <p className="font-mono-tech text-[11px] tracking-wide text-muted-foreground">
        Free consultation · Transparent quotes · No surprises
      </p>
    </form>
  );
}
